import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonCol, IonGrid, IonIcon, IonItemDivider, IonItemGroup, IonList, IonRow } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EStopType, IStopFreightAvailable } from '@okcargo-apps/shared-domain';
import { format, isSameDay, Locale } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { DateFnsModule } from 'ngx-date-fns';
import { map } from 'rxjs';

// TODO move or refactor
type SupportedLang = 'es' | 'en';

// TODO move or refactor
interface LangConfig {
  dateFns: Locale;
  sameDayConnector: [string, string];
  diffDayConnector: string;
  fallbackText: string;
}

@Component({
  selector: 'lib-freight-available-detail-stop',
  templateUrl: './freight-available-detail-stop.component.html',
  styleUrls: ['./freight-available-detail-stop.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    IonList,
    IonItemGroup,
    IonItemDivider,
    TranslateModule,
    IonGrid,
    IonRow,
    IonCol,
    IonIcon,
    DateFnsModule,
  ],
})
export class FreightAvailableDetailStopComponent {
  public readonly stop = input.required<IStopFreightAvailable>();
  public readonly isFreightAvailable = input.required<boolean>();

  private readonly translate = inject(TranslateService);

  // TODO extract stops to diferent components and remove it
  protected readonly EStopType = EStopType;

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang as SupportedLang)),
    { initialValue: (this.translate.getCurrentLang() || this.translate.getDefaultLang() || 'es') as SupportedLang },
  );

  public readonly formattedRange = computed(() => {
    const { since, until } = this.stop();
    const lang = this.currentLang();
    const config = this.getLangConfig(lang);

    if (!since || !until) {
      return config.fallbackText;
    }

    return this.calculateDateString(since, until, config);
  });

  private getLangConfig(lang: SupportedLang): LangConfig {
    const configs: Record<SupportedLang, LangConfig> = {
      es: {
        dateFns: es,
        sameDayConnector: ['de', 'a'],
        diffDayConnector: 'a',
        fallbackText: 'Horario por confirmar',
      },
      en: {
        dateFns: enUS,
        sameDayConnector: ['from', 'to'],
        diffDayConnector: 'to',
        fallbackText: 'Schedule to be confirmed',
      },
    };

    return configs[lang] || configs.es;
  }

  private calculateDateString(start: Date, end: Date, config: LangConfig): string {
    const dayFormat = 'EEE dd/MM/yyyy';
    const timeFormat = "HH:mm'\u00A0h'";

    if (isSameDay(start, end)) {
      const dateStr = format(start, dayFormat, { locale: config.dateFns });
      const startTime = format(start, timeFormat, { locale: config.dateFns });
      const endTime = format(end, timeFormat, { locale: config.dateFns });

      return `${dateStr} ${config.sameDayConnector[0]} ${startTime} ${config.sameDayConnector[1]} ${endTime}`;
    }

    const fullFormat = `${dayFormat} ${timeFormat}`;
    const startStr = format(start, fullFormat, { locale: config.dateFns });
    const endStr = format(end, fullFormat, { locale: config.dateFns });

    return `${startStr} ${config.diffDayConnector} ${endStr}`;
  }
}
